import { FC, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { PurchaseOrderListQuery } from "../graphql/purchase-order.graphql";
import { Container, InfoTooltip } from "../../../core/component";
import { Button, Flex, Space, Table, Tag } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import {
  ClearOutlined,
  CloseOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { translate } from "../../../core";
import { PurchaseOrder } from "../type/purchase-order";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PurchaseSearch } from "./PurchaseSearch";
import {
  purchaseOrderStatusLabelMapper,
  purchaseOrderStatusMapper,
} from "../func/purchaseOrderStatusMapper";
import { sourceTagMap } from "./PurchaseOrderEntryComponent";
import { clearPurchaseOrderDraftStorage } from "./PurchaseOrderMenuItems";
import {
  dateDisplay,
  DateFormatMonthText,
  resolveNumber,
  stringConcat,
  usdFormat,
} from "../../../core/until";
import Enumerable from "linq";
import {
  PURCHASE_ORDER_DEFAULT_PAGE_SIZE,
  PURCHASE_ORDER_PAGE_SIZE_OPTIONS,
  rememberPurchaseOrderListLocation,
  usePurchaseOrderListState,
} from "../hook/purchase-order-list-state.hook";

export const PurchaseOrderList: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  const {
    table,
    request,
    filterTags,
    filterParams,
    hasFilter,
    setTableState,
    applyFilters,
    clearFilters,
    removeFilter,
  } = usePurchaseOrderListState();

  // keeps the back button of the detail page pointed at the state the user left
  useEffect(() => {
    rememberPurchaseOrderListLocation(location.search);
  }, [location.search]);

  const { data, loading } = useQuery(PurchaseOrderListQuery, {
    notifyOnNetworkStatusChange: true,
    variables: { request },
  });

  const total = data?.purchaseOrdersFind.count ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / table.pageSize));

  useEffect(() => {
    if (!loading && table.page > lastPage)
      setTableState({ ...table, page: lastPage });
  }, [loading, lastPage, table, setTableState]);

  const handleTableChange: TableProps<PurchaseOrder>["onChange"] = (
    pagination,
    _filters,
    sorter
  ) => {
    const active = Array.isArray(sorter) ? sorter[0] : sorter;

    setTableState({
      page: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? PURCHASE_ORDER_DEFAULT_PAGE_SIZE,
      sort:
        active?.order && active.columnKey
          ? { field: String(active.columnKey), order: active.order }
          : undefined,
    });
  };

  const columns: ColumnsType<PurchaseOrder> = [
    {
      key: "number",
      title: translate("purchase_order_nu_abbr"),
      dataIndex: "number",
      render: (_, po) => (
        <Link to={`/purchase-order/${po.number}`} onClick={(e) => e.stopPropagation()}>
          #{translate("purchase_order_abbr")}
          {po.number}
        </Link>
      ),
    },
    {
      key: "createdAt",
      title: translate("created_date"),
      dataIndex: "createdAt",
      render: (value) => dateDisplay(value, DateFormatMonthText),
    },
    {
      key: "vendor",
      title: translate("vendor"),
      dataIndex: ["vendor", "contactId"],
      render: (_, po) =>
        po.vendor
          ? stringConcat(po.vendor.contactId, " - ", po.vendor.company)
          : "-",
    },
    {
      key: "status",
      title: translate("status"),
      dataIndex: "status",
      render: (status: PurchaseOrder["status"]) => (
        <InfoTooltip contentKey={`poStatus.${status}`}>
          <Tag bordered color={purchaseOrderStatusMapper[status]} style={{ cursor: "help" }}>
            {purchaseOrderStatusLabelMapper[status]}
          </Tag>
        </InfoTooltip>
      ),
    },
    {
      key: "items",
      title: translate("items_qty_abbr"),
      dataIndex: "items",
      align: "center",
      render: (items: PurchaseOrder["items"]) => items?.length ?? 0,
    },
    {
      key: "totalQuantity",
      title: translate("total_qty_abbr"),
      dataIndex: "items",
      align: "center",
      render: (items: PurchaseOrder["items"]) =>
        Enumerable.from(items ?? []).sum((it) => resolveNumber(it.quantity)),
    },
    {
      key: "receivedQuantity",
      title: translate("received_qty_abbr"),
      dataIndex: "items",
      align: "center",
      render: (items: PurchaseOrder["items"]) => {
        const list = items ?? [];
        const received = Enumerable.from(list).sum((it) =>
          resolveNumber(it.quantityReceived)
        );
        const ordered = Enumerable.from(list).sum((it) =>
          resolveNumber(it.quantity)
        );
        return `${received} / ${ordered}`;
      },
    },
    {
      key: "source",
      title: translate("source"),
      dataIndex: "items",
      render: (items: PurchaseOrder["items"]) => {
        const sources = Enumerable.from(items ?? [])
          .select((it) => it.source)
          .where(Boolean)
          .distinct()
          .toArray();

        if (!sources.length) return "-";

        return (
          <Space size={[4, 4]} wrap>
            {sources.map((source) => (
              <Tag key={source} bordered color={sourceTagMap[source]?.color as any}>
                {sourceTagMap[source]?.display ?? source}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      key: "totalCost",
      title: translate("total_cost"),
      dataIndex: "items",
      align: "right",
      render: (items: PurchaseOrder["items"]) =>
        usdFormat(
          Enumerable.from(items ?? []).sum(
            (it) => resolveNumber(it.price) * resolveNumber(it.quantity)
          )
        ),
    },
  ];

  const sortedColumns = columns.map((column) => ({
    ...column,
    sortOrder:
      table.sort && column.key === table.sort.field ? table.sort.order : null,
  }));

  return (
    <>
      <Container loading={loading}>
        <Flex justify="end" gap={10} className="mb-3">
          {hasFilter ? (
            <Button icon={<ClearOutlined />} onClick={clearFilters}>
              {translate("clear", "filter")}
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => setSearchOpen(true)}
          >
            {translate("search")}
          </Button>
          <Link to="/purchase-order/new" onClick={clearPurchaseOrderDraftStorage}>
            <Button type="primary" icon={<PlusOutlined />}>
              {translate("create", "new", "purchase_order_abbr")}
            </Button>
          </Link>
        </Flex>

        {hasFilter ? (
          <Flex align="center" gap={8} wrap="wrap" className="mb-3">
            {filterTags.map((tag) => (
              <Tag
                key={tag.key}
                bordered
                closable
                closeIcon={
                  <CloseOutlined
                    role="button"
                    tabIndex={0}
                    aria-label={`${translate("remove")} ${tag.label}`}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      removeFilter(tag.key);
                    }}
                  />
                }
                onClose={() => removeFilter(tag.key)}
              >
                {tag.label}: {tag.value}
              </Tag>
            ))}
          </Flex>
        ) : null}

        <Table<PurchaseOrder>
          rowKey="id"
          columns={sortedColumns}
          dataSource={data?.purchaseOrdersFind.data ?? []}
          onRow={(po) => ({
            onClick: () => navigate(`/purchase-order/${po.number}`),
            style: { cursor: "pointer" },
          })}
          onChange={handleTableChange}
          pagination={{
            current: table.page,
            pageSize: table.pageSize,
            total: data?.purchaseOrdersFind.count ?? 0,
            showSizeChanger: true,
            pageSizeOptions: PURCHASE_ORDER_PAGE_SIZE_OPTIONS,
          }}
        />
      </Container>

      <PurchaseSearch
        key={JSON.stringify(filterParams)}
        open={searchOpen}
        closeMe={() => setSearchOpen(false)}
        initial={filterParams}
        onComplete={applyFilters}
      />
    </>
  );
};
